import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import * as surveyConfiguration from '../../../survey-configuration'

import { getActionsConfig } from './actions'
import type { Api } from './api'
import * as effects from './effects'
import * as extraReducers from './extra-reducers'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Manages warnings for a space entity as a plugin of that entity collection.
 */
export class Plugin<
  TTypeParams extends
    ec.models.TypeParams.Constraint = ec.models.TypeParams.Constraint,
> extends ec.plugins.Plugin<
  TTypeParams,
  typeof Plugin.id,
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link ec.plugins.Plugin.Id.Custom} */
  public static id = ec.plugins.Plugin.Id.Custom.create('SpaceWarnings')

  /** @see {@link Api} */
  public readonly api: Api

  /**
   * Convenience accessor for the feature's action creators.
   * Allows consumers to call `plugin.actions.xyz` instead of `plugin.feature.actions.xyz`.
   */
  public get actions(): this['feature']['actions'] {
    return this.feature.actions
  }

  /**
   * Convenience accessor for the feature's selectors. Allows consumers to call
   * `plugin.selectors.xyz` instead of `plugin.feature.selectors.xyz`.
   */
  public get selectors(): this['feature']['selectors'] {
    return this.feature.selectors
  }

  constructor({
    api,
    collection,
  }: Readonly<Plugin.constructor.Params<TTypeParams>>) {
    super({
      actions: getActionsConfig(),
      collection,
      id: Plugin.id,
      initialState: State.INITIAL,
      selectors: feature => new Selectors(feature),
    })

    const surveyConfigFeature = ngCore.inject(surveyConfiguration.Feature)
    this.api = api
    this.feature.registerExtraReducers(extraReducers.resetOnContextChange(this))
    this.feature.registerEffects(
      effects.loadForCurrentEntity(this),
      effects.loadForCurrentEntityOnContextChange(this),
      effects.loadForCurrentEntityOnEntityChange(this),
      effects.loadForCurrentEntityOnSurveyConfigurationChange(
        surveyConfigFeature,
      ),
      effects.executeLoad(this),
    )
  }
}

export namespace Plugin {
  export namespace constructor {
    export interface Params<
      TTypeParams extends ec.models.TypeParams.Constraint,
    > {
      readonly api: Api
      readonly collection: ec.Collection<TTypeParams>
    }
  }
}
